/**
 * @file ソート可能コンポーネント用の統合フック集
 * ドラッグ&ドロップ、クリック防止、順序管理などすべてのフックを集約
 */

import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshWorkspaceSidebar } from "@/app/[lang]/[workspaceId]/_contexts/workspace-sidebar-data-context";
import { logger } from "@/lib/logger";
import { useViewOrder } from "../../_contexts/view-order-context";
import type { AnySidebarItem, UpdateViewOrderFunction } from "./sortable-types";
import { categorizeItems, reconstructItemOrder } from "./sortable-utils";

// クリックイベントのタイミング設定
const DRAG_END_CLICK_DELAY = 300; // ドラッグ終了後、クリック無効化する時間(ms)

// ============================================================================
// ドラッグ&ドロップフック
// ============================================================================

interface UseDragAndDropProps<T extends AnySidebarItem> {
  items: T[];
  setItems: (items: T[]) => void;
  saveOrder: (items: T[]) => void;
  isEnabled?: boolean;
}

interface UseDragAndDropReturn {
  activeId: string | null;
  isDragging: boolean;
  dragEndTimeRef: React.MutableRefObject<number>;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
}

/**
 * ドラッグ&ドロップのロジックをカプセル化
 */
export function useDragAndDrop<T extends AnySidebarItem>({
  items,
  setItems,
  saveOrder,
  isEnabled = true,
}: UseDragAndDropProps<T>): UseDragAndDropReturn {
  const [activeId, setActiveId] = useState<string | null>(null);
  const dragEndTimeRef = useRef<number>(0);
  const isDragging = activeId !== null;

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      if (!isEnabled) {
        return;
      }
      setActiveId(event.active.id as string);
      dragEndTimeRef.current = 0;
    },
    [isEnabled],
  );

  const handleDragCancel = useCallback(() => {
    dragEndTimeRef.current = Date.now();
    if (!isEnabled) {
      return;
    }
    setActiveId(null);
  }, [isEnabled]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      dragEndTimeRef.current = Date.now();

      if (!isEnabled) {
        return;
      }

      setActiveId(null);

      if (!over || active.id === over.id) {
        return;
      }

      // ソート可能・不可アイテムを分離
      const { sortable: sortableItems, nonSortable: nonSortableItems } =
        categorizeItems(items);

      // 並び替え用のインデックスを検索
      const oldIndex = sortableItems.findIndex(
        (item: T) => item.id === active.id,
      );
      const newIndex = sortableItems.findIndex(
        (item: T) => item.id === over.id,
      );

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // ソート可能アイテムを並び替え
      const newSortableItems = arrayMove(sortableItems, oldIndex, newIndex);

      // 適切な順序でitems配列を再構築
      const newItems = reconstructItemOrder(newSortableItems, nonSortableItems);

      // 即座にフィードバックを得るためローカル状態を更新
      setItems(newItems);

      // バックグラウンドでサーバーに保存（awaitしない）
      saveOrder(newItems);
    },
    [isEnabled, items, setItems, saveOrder],
  );

  return {
    activeId,
    isDragging,
    dragEndTimeRef,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  };
}

// ============================================================================
// クリック防止フック
// ============================================================================

interface UsePreventClickDuringDragProps {
  isDragging: boolean;
  dragEndTimeRef: React.MutableRefObject<number>;
}

/**
 * ドラッグ中およびドラッグ直後のクリックを防止
 * キャプチャーフェーズでイベントを処理して確実に防止
 */
export function usePreventClickDuringDrag({
  isDragging,
  dragEndTimeRef,
}: UsePreventClickDuringDragProps): React.RefObject<HTMLDivElement | null> {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClickCapture = (e: MouseEvent) => {
      // ドラッグ中またはドラッグ直後のクリックを防ぐ
      if (
        isDragging ||
        Date.now() - dragEndTimeRef.current < DRAG_END_CLICK_DELAY
      ) {
        e.preventDefault();
        e.stopPropagation();

        // アンカー要素の場合はフォーカスも解除
        if (e.target instanceof HTMLAnchorElement) {
          e.target.blur();
        }
      }
    };

    // キャプチャーフェーズでリスナーを追加（子要素より先に処理）
    container.addEventListener("click", handleClickCapture, true);

    return () => {
      container.removeEventListener("click", handleClickCapture, true);
    };
  }, [isDragging, dragEndTimeRef]);

  return containerRef;
}

// ============================================================================
// 順序管理フック
// ============================================================================

/**
 * ビュー順序更新の共通ロジックを提供するフック
 * SAとUAの両方で使用される楽観的更新とエラーハンドリングを含む
 */
export function useUpdateViewOrder(
  updateViewOrderAction: UpdateViewOrderFunction,
) {
  const { setViewOrder, updateViewOrder } = useViewOrder();
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();

  const handleUpdateViewOrder: UpdateViewOrderFunction = useCallback(
    async (actionId, ids, projectId) => {
      // 楽観的更新をトリガー（IDのみ）
      updateViewOrder(projectId, ids);

      try {
        // サーバーに更新を送信
        const result = await updateViewOrderAction(actionId, ids, projectId);

        if (result.error) {
          throw new Error(result.error);
        }

        // 成功したら実際の状態を更新（IDのみ）
        setViewOrder(projectId, ids);
        refreshWorkspaceSidebar("order-mutation");

        return result;
      } catch (error) {
        // エラー時は自動的にロールバックされる
        logger.error("View order update failed:", error);
        return {
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        };
      }
    },
    [
      updateViewOrderAction,
      setViewOrder,
      updateViewOrder,
      refreshWorkspaceSidebar,
    ],
  );

  return handleUpdateViewOrder;
}
